import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Scale, Plus, Search, Loader2, Trash2, Edit2, MapPin, X, Building
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { confirmToast } from '@/utils/confirmToast';
import Sidebar from '@/components/Sidebar';

export default function CourtsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courts, setCourts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddCourt, setShowAddCourt] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  
  const emptyCourt = {
    county: '',
    court_name: '',
    street_address: '',
    mailing_address: '',
    city: '',
    state: 'CA',
    zip: '',
    branch: ''
  };
  
  const [courtForm, setCourtForm] = useState(emptyCourt);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await fetchCourts();
      await fetchStateOptions();
    };
    initData();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData.app_mode !== 'crm') {
          navigate('/dashboard');
          return;
        }
        setUser(userData);
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCourts = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/courts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCourts(data);
      }
    } catch (error) {
      console.error('Error fetching courts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStateOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/global-fields/option-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const usaStatesList = data.system_lists.find(list => list.name === 'UsaStates');
        if (usaStatesList) {
          setStateOptions(usaStatesList.options);
        }
      }
    } catch (error) {
      console.error('Failed to load state options');
    }
  };

  const handleSaveCourt = async () => {
    if (!courtForm.county.trim() || !courtForm.court_name.trim()) {
      toast.error('County and Court Name are required');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const url = editingCourt 
        ? `${API}/crm/courts/${editingCourt.court_id}`
        : `${API}/crm/courts`;
      
      const response = await fetch(url, {
        method: editingCourt ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(courtForm)
      });
      
      if (response.ok) {
        toast.success(editingCourt ? 'Court updated' : 'Court created');
        setShowAddCourt(false);
        setEditingCourt(null);
        setCourtForm(emptyCourt);
        fetchCourts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save court');
      }
    } catch (error) {
      toast.error('Failed to save court');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourt = async (courtId) => {
    const court = courts.find(c => c.court_id === courtId);
    const courtName = court?.court_name || 'this court';
    
    confirmToast(
      `Are you sure you want to delete "${courtName}"? This action cannot be undone.`,
      async () => {
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API}/crm/courts/${courtId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Court deleted successfully');
            fetchCourts();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete court');
          }
        } catch (error) {
          toast.error('Failed to delete court');
        }
      },
      { title: 'Delete Court' }
    );
  };

  const handleEditCourt = (court) => {
    setCourtForm({
      county: court.county || '',
      court_name: court.court_name || '',
      street_address: court.street_address || '',
      mailing_address: court.mailing_address || '',
      city: court.city || '',
      state: court.state || 'CA',
      zip: court.zip || '',
      branch: court.branch || ''
    });
    setEditingCourt(court);
    setShowAddCourt(true);
  };

  const handleCancel = () => {
    setShowAddCourt(false);
    setEditingCourt(null);
    setCourtForm(emptyCourt);
  };

  const filteredCourts = courts.filter(court =>
    court.court_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    court.county.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (court.city && court.city.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {user && <Sidebar user={user} />}
      <div className={`flex-1 flex flex-col overflow-hidden ${user ? 'lg:ml-64' : ''}`}>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                  <Scale className="w-8 h-8 text-violet-600" />
                  Courts Directory
                </h1>
                <p className="text-slate-500 mt-1">Manage court information for your cases</p>
              </div>
              <Button
                onClick={() => setShowAddCourt(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-11"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Court
              </Button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courts by name, county, or city..."
                  className="pl-10 h-12 text-base"
                />
              </div>
            </div>

            {/* Courts List */}
            {filteredCourts.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
                <Scale className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {searchQuery ? 'No courts found' : 'No courts added yet'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {searchQuery ? 'Try a different search term' : 'Add your first court to get started'}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => setShowAddCourt(true)}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Court
                  </Button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Court Name</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">County</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Branch</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Location</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredCourts.map((court) => (
                      <tr key={court.court_id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Scale className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-slate-900">{court.court_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {court.county} County
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {court.branch || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {court.city}{court.city && court.state ? ', ' : ''}{court.state}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCourt(court)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-court-${court.court_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCourt(court.court_id)}
                              className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                              data-testid={`delete-court-${court.court_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add/Edit Court Modal */}
            {showAddCourt && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold">
                      {editingCourt ? 'Edit Court' : 'Add New Court'}
                    </h2>
                    <Button variant="ghost" size="sm" onClick={handleCancel}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="p-6 space-y-4">
                    {/* County */}
                    <div>
                      <Label>County *</Label>
                      <Input
                        value={courtForm.county}
                        onChange={(e) => setCourtForm({ ...courtForm, county: e.target.value })}
                        placeholder="e.g., Los Angeles"
                      />
                    </div>

                    {/* Court Name */}
                    <div>
                      <Label>Court Name *</Label>
                      <Input
                        value={courtForm.court_name}
                        onChange={(e) => setCourtForm({ ...courtForm, court_name: e.target.value })}
                        placeholder="e.g., Superior Court of California"
                      />
                    </div>

                    {/* Street Address */}
                    <div>
                      <Label>Street Address</Label>
                      <Input
                        value={courtForm.street_address}
                        onChange={(e) => setCourtForm({ ...courtForm, street_address: e.target.value })}
                        placeholder="Street address for in-person visits"
                      />
                    </div>

                    {/* Mailing Address */}
                    <div>
                      <Label>Mailing Address</Label>
                      <Input
                        value={courtForm.mailing_address}
                        onChange={(e) => setCourtForm({ ...courtForm, mailing_address: e.target.value })}
                        placeholder="Mailing address for filings"
                      />
                    </div>

                    {/* City, State, ZIP */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>City</Label>
                        <Input
                          value={courtForm.city}
                          onChange={(e) => setCourtForm({ ...courtForm, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <select
                          value={courtForm.state}
                          onChange={(e) => setCourtForm({ ...courtForm, state: e.target.value })}
                          className="h-11 rounded-lg border border-slate-200 px-3 text-sm w-full"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>ZIP</Label>
                        <Input
                          value={courtForm.zip}
                          onChange={(e) => setCourtForm({ ...courtForm, zip: e.target.value })}
                          placeholder="ZIP"
                        />
                      </div>
                    </div>

                    {/* Branch */}
                    <div>
                      <Label>Branch/Department</Label>
                      <Input
                        value={courtForm.branch}
                        onChange={(e) => setCourtForm({ ...courtForm, branch: e.target.value })}
                        placeholder="e.g., Central District"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex gap-3">
                    <Button
                      onClick={handleSaveCourt}
                      disabled={saving}
                      className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-11"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          {editingCourt ? 'Update' : 'Create'} Court
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="rounded-xl h-11 px-8"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
